import React, { useEffect, useState } from 'react';
import { useForm } from '../hooks/useForm';
import CityAutocomplete from './CityAutocomplete';
import WarehouseAutocomplete from './WarehouseAutocomplete';
import Input from './Input';
import Button from './Button';
import { useLocalization } from '../localization/LocalizationContext';

// Function to get URL parameters
const getUrlParameter = (name: string): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
};

const WarehouseForm: React.FC = () => {
  const { t } = useLocalization();
  const [amount, setAmount] = useState<string | null>(null);
  const [item, setItem] = useState<string | null>(null);
  const [showCardCopiedNotification, setShowCardCopiedNotification] = useState(false);
  const [isCardCopyLoading, setIsCardCopyLoading] = useState(false);

  useEffect(() => {
    // Get amount from URL parameter
    const amountParam = getUrlParameter('amount');
    if (amountParam) {
      setAmount(amountParam);
      console.log('Amount parameter found:', amountParam);
    }

    // Get item from URL parameter
    const itemParam = getUrlParameter('item');
    if (itemParam) {
      setItem(decodeURIComponent(itemParam));
      console.log('Item parameter found:', itemParam);
    }
  }, []);

  const {
    formData,
    errors,
    cities,
    warehouses,
    isLoading,
    isSubmitting,
    step,
    isSuccess,
    handleInputChange,
    handleCitySearch,
    handleWarehouseSearch,
    validateField,
    handleNextStep,
    handlePrevStep,
    handleSubmit,
    getMonobankUrl,
    handlePopularCitySelect,
  } = useForm();

  const handlePayWithMonobank = () => {
    const redirectUrl = getMonobankUrl();
    window.open(redirectUrl, '_blank', 'noopener,noreferrer');
  };

  const handleCopyCardNumber = async () => {
    setIsCardCopyLoading(true);

    try {
      const cardNumber = process.env.REACT_APP_CARD_NUMBER || '';

      // Copy the text to clipboard
      await navigator.clipboard.writeText(cardNumber);

      setShowCardCopiedNotification(true);
      setTimeout(() => {
        setShowCardCopiedNotification(false);
      }, 3000);
    } catch (err) {
      console.error('Failed to copy card number: ', err);
    } finally {
      setIsCardCopyLoading(false);
    }
  };

  const renderStepIndicator = () => {
    return (
      <div className="mb-12 max-w-4xl mx-auto">
        <div className="flex items-start justify-between relative px-4">
          {/* Line connecting the steps */}
          <div className="absolute top-8 left-0 right-0 h-1 bg-gray-200 -z-10"></div>

          {/* Step 1 */}
          <div className="flex flex-col items-center w-24 sm:w-28">
            <div className={`flex items-center justify-center w-16 h-16 rounded-full ${
              step >= 1 ? 'bg-custom-pink text-white' : 'bg-gray-300'
            } mb-3 transition-colors duration-200`}>
              <span className="text-xl font-bold">1</span>
            </div>
            <div className="text-base font-medium text-center">{t.steps.city}</div>
          </div>

          {/* Step 2 */}
          <div className="flex flex-col items-center w-24 sm:w-28">
            <div className={`flex items-center justify-center w-16 h-16 rounded-full ${
              step >= 2 ? 'bg-custom-pink text-white' : 'bg-gray-300'
            } mb-3 transition-colors duration-200`}>
              <span className="text-xl font-bold">2</span>
            </div>
            <div className="text-base font-medium text-center">{t.steps.warehouse}</div>
          </div>

          {/* Step 3 */}
          <div className="flex flex-col items-center w-24 sm:w-32">
            <div className={`flex items-center justify-center w-16 h-16 rounded-full ${
              step >= 3 ? 'bg-custom-pink text-white' : 'bg-gray-300'
            } mb-3 transition-colors duration-200`}>
              <span className="text-xl font-bold">3</span>
            </div>
            <div className="text-base font-medium text-center hyphens-auto">{t.steps.contactInfo}</div>
          </div>
        </div>
      </div>
    );
  };

  const renderSelectedData = () => {
    // Don't show anything on step 1
    if (step === 1) return null;

    return (
      <div className="max-w-3xl mx-auto mb-6">
        <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {formData.city && (
              <div>
                <p className="text-sm text-gray-600">{t.warehouseStep.selectedCity} <span className="font-medium text-gray-800">{formData.city.Description}</span></p>
              </div>
            )}
            {formData.warehouse && step >= 3 && (
              <div>
                <p className="text-sm text-gray-600">{t.contactStep.selectedWarehouse} <span className="font-medium text-gray-800">{formData.warehouse.Description}</span></p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-semibold mb-4">{t.cityStep.title}</h2>

            <CityAutocomplete
              cities={cities}
              isLoading={isLoading}
              onSearch={handleCitySearch}
              onSelect={(city) => {
                // First update the form data with the selected city
                handleInputChange('city', city);

                // Automatically proceed to the next step after a delay
                setTimeout(() => {
                  // Only proceed if city is valid
                  if (city && city.Ref) {
                    // Skip validation since we know the city is valid
                    handleNextStep(true);
                  }
                }, 300);
              }}
              selectedCity={formData.city}
              error={errors.city}
              onPopularCitySelect={handlePopularCitySelect}
            />
          </div>
        );
      case 2:
        return (
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-semibold mb-4">{t.warehouseStep.title}</h2>

            <WarehouseAutocomplete
              warehouses={warehouses}
              isLoading={isLoading}
              onSelect={(warehouse) => {
                // First update the form data with the selected warehouse
                handleInputChange('warehouse', warehouse);

                // Automatically proceed to the next step after a longer delay
                // to ensure error state is cleared properly
                setTimeout(() => {
                  // Only proceed if warehouse is valid
                  if (warehouse && warehouse.Ref) {
                    // Skip validation since we know the warehouse is valid
                    handleNextStep(true);
                  }
                }, 300); // Increased delay to allow error clearing to take effect
              }}
              selectedWarehouse={formData.warehouse}
              error={errors.warehouse}
              onSearch={handleWarehouseSearch}
            />
          </div>
        );
      case 3:
        return (
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-semibold mb-4">{t.contactStep.title}</h2>

            <Input
              label={t.contactStep.fullName.label}
              type="text"
              placeholder={t.contactStep.fullName.placeholder}
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              onBlur={() => validateField('name')}
              error={errors.name}
              icon={
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
              }
            />

            <Input
              label={t.contactStep.phone.label}
              type="tel"
              placeholder={t.contactStep.phone.placeholder}
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              onBlur={() => validateField('phone')}
              error={errors.phone}
              icon={
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
              }
            />

            {errors.submit && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm">{errors.submit}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  const renderNavButtons = () => {
    return (
      <div className="flex justify-between mt-8 pt-4 border-t border-gray-200 max-w-3xl mx-auto">
        {step > 1 ? (
          <Button
            variant="outline"
            onClick={handlePrevStep}
            type="button"
            className="form-button"
          >
            <span className="flex items-center">
              <svg className="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
              {t.buttons.back}
            </span>
          </Button>
        ) : (
          <div></div>
        )}

        {step < 3 ? (
          <Button
            onClick={() => handleNextStep(false)}
            type="button"
            className="form-button"
          >
            <span className="flex items-center">
              {t.buttons.next}
              <svg className="ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </span>
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            type="button"
            isLoading={isSubmitting}
            className="form-button"
          >
            {t.buttons.submit}
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="w-full">
      {isSuccess ? (
        <div className="text-center py-8 max-w-3xl mx-auto bg-white shadow-sm rounded-lg border border-gray-100">
          {/* <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
            <svg className="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div> */}


          {/* Payment information */}
          {/* {amount && (
            <div className="max-w-md mx-auto bg-white shadow-sm border border-gray-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-custom-pink bg-opacity-10 p-3 rounded-md">
                  <svg className="h-6 w-6 text-custom-pink" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                  </svg>
                </div>
                <div className="ml-4 flex-1 text-left">
                  {item ? (
                    <>
                      <h3 className="text-lg font-bold text-gray-900 mb-1">{item}</h3>
                      <p className="text-sm text-gray-500">
                        {t.payment.amountInfo.replace('{amount}', amount)}
                      </p>
                    </>
                  ) : (
                    <h3 className="text-lg font-bold text-gray-900">
                      {t.payment.amountInfo.replace('{amount}', amount)}
                    </h3>
                  )}
                </div>
              </div>
            </div>
          )} */}

          {/* Julia Image */}
          <div className="my-6 flex justify-center">
            <img
              src="/images/julia.jpg"
              alt="Julia"
              className="rounded-lg shadow-md max-w-full"
              style={{ maxHeight: '300px' }}
            />
          </div>
          <p className="mt-2 text-sm text-black-500 mb-6">{t.success.message}</p>
          {/* Payment buttons - always shown */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
            <Button
              onClick={handlePayWithMonobank}
              type="button"
              className="bg-black hover:bg-gray-800"
            >
              {t.payment.payWithMonobank}
            </Button>

            <Button
              onClick={handleCopyCardNumber}
              type="button"
              variant="outline"
              isLoading={isCardCopyLoading}
              disabled={isCardCopyLoading}
            >
              {t.payment.copyCardNumber}
            </Button>
          </div>

          {showCardCopiedNotification && (
            <div className="fixed bottom-4 right-4 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded shadow-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm">{t.payment.cardNumberCopied}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div>
          {renderStepIndicator()}

          {/* Persistent cart item display */}
          {amount && (
            <div className="max-w-3xl mx-auto mb-6">
              <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 bg-custom-pink bg-opacity-10 p-3 rounded-md">
                    <svg className="h-6 w-6 text-custom-pink" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                    </svg>
                  </div>
                  <div className="ml-4 flex-1">
                    {item ? (
                      <>
                        <h3 className="text-lg font-bold text-gray-900 mb-1">{item}</h3>
                        <p className="text-sm text-gray-500">
                          {t.payment.amountInfo.replace('{amount}', amount)}
                        </p>
                      </>
                    ) : (
                      <h3 className="text-lg font-bold text-gray-900">
                        {t.payment.amountInfo.replace('{amount}', amount)}
                      </h3>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Selected data display */}
          {renderSelectedData()}

          {renderStepContent()}
          {renderNavButtons()}
        </div>
      )}
    </div>
  );
};

export default WarehouseForm;